import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Select from 'react-select';

import { RefinementListExposed, RefinementListProvided } from 'react-instantsearch-core';
import { isEqual } from 'lodash';
import { SelectApplicableFiltersExposedStateManagement, Option } from '../../admin-dashboard-tables/interfaces';
import CheckBoxOption from './react-select-custom-child-components/checkbox-option';
import CustomMenuList from './react-select-custom-child-components/search-menu-list';
import NewValueContainer from './react-select-custom-child-components/new-value-container';
import MenuWithFooterButton from './react-select-custom-child-components/MenuWithFooterButton';

type SelectDropdownProps = SelectApplicableFiltersExposedStateManagement
  & RefinementListExposed
  & RefinementListProvided;

function getSelectDropdown(
  hasBorder: boolean,
  placeholder: string = '',
  extraOptions: Option[] = [],
  label = '',
  sortOptions = false,
  isFullWidth = false,
) {
  const SelectDropdown: React.FC<SelectDropdownProps> = ({
    attribute,
    items,
    operator,
    updatePendingRefinements,
    applyFilters,
    isDirty,
    currentRefinement,
  }) => {
    const options: Option[] = [
      ...extraOptions,
      ...items.map((item) => ({
        value: item.label,
        label: item.label,
      })),
    ];

    const sortedOptions = useMemo(() => {
      if (sortOptions) {
        return options.sort(
          (a, b) => {
            if (a.label < b.label) {
              return -1;
            }
            if (a.label > b.label) {
              return 1;
            }
            return 0;
          },
        );
      }
      return options;
    }, [sortOptions, options]);

    // Set component length based on the placeholder
    const length = `${(9 * placeholder.length) + 60}px`;

    // Custom sub-component styles
    const selectStyles = {
      control: (styles) => {
        return {
          ...styles,
          width: isFullWidth ? '100%' : length,
        };
      },
      option: (styles) => {
        return {
          ...styles,
          backgroundColor: 'white',
          color: 'inherit',
        };
      },
    };

    const containerRef = useRef<HTMLDivElement>(null);
    // Is the select box focused (and open?)
    const [isFocused, setIsFocused] = useState(false);
    // Internal state of the dropdown component
    const [selectedValue, setSelectedValue] = useState(() => {
      return items.filter((item) => item.isRefined)
        .map((item) => ({
          value: item.label,
          label: item.label,
        }));
    });
    // Contents of the select input search
    const [inputValue, setInputValue] = useState('');
    const [memoizedCurrentRefinement, setMemoizedCurrentRefinement] = useState(currentRefinement);

    /**
     * Open or close the menu any time a click event is registered. Based
     * on the following conditions:
     *
     * Close the component and reset to empty search if
     * 1) The click is outside the component
     * 2) The menu (options dropdown) is not open
     * 3) The click is outside the menu
     *
     * Otherwise, keep it open
     */
    const onDomClick = (e) => {
      if (!containerRef || !containerRef.current) {
        return;
      }

      const menu = containerRef.current.querySelector('.algolia-select__menu');

      if (
        !containerRef.current.contains(e.target)
        || !menu
        || !menu.contains(e.target)
      ) {
        setIsFocused(false);
        setInputValue('');
      } else {
        setIsFocused(true);
      }
    };

    useEffect(() => {
      document.addEventListener('mousedown', onDomClick);

      return () => {
        document.removeEventListener('mousedown', onDomClick);
      };
    }, []);

    useEffect(() => {
      if (updatePendingRefinements) {
        updatePendingRefinements(
          attribute,
          selectedValue.map((valueObject) => valueObject.value),
          operator === 'and',
        );
      }
    }, [selectedValue]);

    useEffect(() => {
      // `currentRefinement` triggers an effect on every render before initial refinement, perhaps
      // because it is initialized an as empty array.
      if (!isEqual(currentRefinement, memoizedCurrentRefinement)) {
        setMemoizedCurrentRefinement(currentRefinement);
      }
    }, [currentRefinement, memoizedCurrentRefinement]);

    useEffect(() => {
      const newSelectedValue = memoizedCurrentRefinement
        .map((refinement) => ({
          value: refinement,
          label: refinement,
        }));

      if (!isEqual(newSelectedValue, selectedValue)) {
        setSelectedValue(newSelectedValue);
      }
    }, [memoizedCurrentRefinement]);

    useEffect(() => {
      // When filters are reset, we need also to reset selectedValue items that haven't
      // been applied, otherwise, they appear checked, but are not tracked by
      // `useApplicableFilters`
      if (!isDirty && selectedValue.length !== memoizedCurrentRefinement.length) {
        setSelectedValue((currentSelectedValue) => {
          return currentSelectedValue.filter((currentValue) => {
            return memoizedCurrentRefinement.includes(currentValue.value);
          });
        });
      }
    }, [isDirty]);

    const onChangeHandler = (event: Option|null) => {
      // A `null` event signifies that the Select component has been "cleared"/"reset"
      if (!event || event.value === 'all') {
        setSelectedValue([]);
      } else {
        setSelectedValue((currentValues) => {
          // If value is currently selected, remove it from state
          if (currentValues.find((selectedFacet) => selectedFacet.value === event.value)) {
            return currentValues.filter((selectedFacet) => {
              return selectedFacet.value !== event.value;
            });
          }
          // If value is new, add it to state
          return [
            ...currentValues,
            event,
          ];
        });
      }
    };

    /**
     * If a select-dropdown menu is opened, but it is not this menu,
     * close this menu.
     *
     * There is currently an issue with events bubbling when <SelectDropdown> component
     * controls are clicked, and so a custom event is emitted instead.
     *
     * This is a temporary work-around, and a long-term task exists that will involve
     * re-writing the <SelectDropdown> component:
     * cf. https://schweb.atlassian.net/browse/LPBUGS-1453
     *
     * @param event
     */
    const onSelectMenuOpen = (event) => {
      if (event?.detail?.emitter !== attribute) {
        setIsFocused(false);
      }
    };

    useEffect(() => {
      document.addEventListener('select-dropdown-opened', onSelectMenuOpen);

      return () => {
        document.removeEventListener('select-dropdown-opened', onSelectMenuOpen);
      };
    }, []);

    return (
      <div ref={containerRef}>
        <Select
          aria-label={label || placeholder}
          className={`algolia-select${hasBorder ? '' : ' no-border'}`}
          classNamePrefix="algolia-select"
          value={selectedValue}
          components={{
            Menu: MenuWithFooterButton,
            MenuList: CustomMenuList,
            ValueContainer: NewValueContainer,
            Option: CheckBoxOption,
          }}
          onChange={(event) => {
            setIsFocused(false); // TODO: Needed?
            onChangeHandler(event);
          }}
          isSearchable={false} // Turns default search off. Use custom search in MenuList component
          options={sortedOptions}
          placeholder={placeholder}
          styles={selectStyles}
          onInputChange={(val) => setInputValue(val)}
          menuIsOpen={isFocused || undefined}
          isFocused={isFocused || undefined}
          onMenuClose={() => setIsFocused(false)}
          onMenuOpen={() => {
            setIsFocused(true);
            containerRef.current?.dispatchEvent(new CustomEvent('select-dropdown-opened', {
              bubbles: true,
              detail: {
                emitter: attribute,
              },
            }));
          }}
          inputValue={inputValue}
          onMenuInputFocus={() => setIsFocused(true)}
          setInputValue={setInputValue}
          setIsFocused={setIsFocused}
          closeMenuOnSelect={false}
          // These props are used in the `<MenuWithFooterButton>`.
          // They can be accessed via the `props.selectProps` object.
          onPressFooterButton={applyFilters}
          footerButtonText="Apply"
          hideFooterButton={!isDirty}
        />
      </div>
    );
  };
  return SelectDropdown;
}

export default getSelectDropdown;
